import { Icon, LoadingPlaceholder, Tooltip } from "@indigo-components";
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import type { PDFSearchResult } from "~/indigo-components/src/components/InlinePDFViewer/PDFSearchBar";
import "./ReferencePdfFloatingViewer.css";

const PDFViewerWithControls = lazy(() =>
  import(
    "~/indigo-components/src/components/InlinePDFViewer/PDFViewerWithControls"
  ).then((m) => ({ default: m.PDFViewerWithControls })),
);

// Dimensions tuned so a full A4 portrait page fills the body width without
// vertical scrolling: panel aspect ratio ≈ page aspect ratio (~1.414). A
// landscape-shaped default wastes horizontal space because the height-fit
// cap then dominates and leaves the rendered page narrow with wide gutters.
const PANEL_WIDTH = 560;
const PANEL_HEIGHT = 900;
const MIN_PANEL_WIDTH = 360;
const MIN_PANEL_HEIGHT = 320;
const PANEL_EDGE_MARGIN = 24;
const DRAG_BOTTOM_OFFSET = 48;
const RESIZE_HANDLE_SIZE = 5;
const RESIZE_CORNER_SIZE = 12;
// Horizontal padding inside InlinePDFViewer's page list (p-8 = 32px/side).
// Subtract from the body width to avoid horizontal scroll.
const VIEWER_PAGE_PADDING = 64;
// Fallback reserve when the caller doesn't supply a sidebar ref — covers the
// overlay-mode 350 px width plus a little breathing room. The static-mode IR
// sidebar takes flex space and can be wider, so the actual rendered width is
// preferred whenever a ref is provided (see `anchorRef` prop below).
const SIDEBAR_RESERVED_WIDTH = 360;
const PANEL_GAP_TO_SIDEBAR = 16;
// A4 portrait aspect ratio (height/width). Used as the worst-case assumption
// when sizing `pageWidth` from the body height so a full page fits without
// vertical scrolling. Landscape pages end up smaller and centered, which is
// fine — the goal is "no scroll to see the rest of the page" for the common
// portrait case (Rating tables, most AMA Guide content).
const PAGE_ASPECT_RATIO = 1.414;
// Vertical chrome above the PDF page list inside PDFViewerWithControls:
// one combined control-bar row (control buttons + inline search input) —
// not two stacked rows, so the budget stays tight.
const PDF_CHROME_HEIGHT = 64;
// Breathing room so the rendered page doesn't sit flush against the bottom of
// the panel body (page shadow + PAGE_GAP between pages in the list).
const PAGE_VERTICAL_PADDING = 16;

interface ResizeEdges {
  top?: boolean;
  right?: boolean;
  bottom?: boolean;
  left?: boolean;
}

interface ResizeHandleConfig {
  key: string;
  edges: ResizeEdges;
  cursor: string;
  style: React.CSSProperties;
}

const RESIZE_HANDLES: ResizeHandleConfig[] = [
  {
    key: "e-right",
    edges: { right: true },
    cursor: "ew-resize",
    style: {
      right: 0,
      top: RESIZE_CORNER_SIZE,
      bottom: RESIZE_CORNER_SIZE,
      width: RESIZE_HANDLE_SIZE,
    },
  },
  {
    key: "e-bottom",
    edges: { bottom: true },
    cursor: "ns-resize",
    style: {
      bottom: 0,
      left: RESIZE_CORNER_SIZE,
      right: RESIZE_CORNER_SIZE,
      height: RESIZE_HANDLE_SIZE,
    },
  },
  {
    key: "e-left",
    edges: { left: true },
    cursor: "ew-resize",
    style: {
      left: 0,
      top: RESIZE_CORNER_SIZE,
      bottom: RESIZE_CORNER_SIZE,
      width: RESIZE_HANDLE_SIZE,
    },
  },
  {
    key: "e-top",
    edges: { top: true },
    cursor: "ns-resize",
    style: {
      top: 0,
      left: RESIZE_CORNER_SIZE,
      right: RESIZE_CORNER_SIZE,
      height: RESIZE_HANDLE_SIZE,
    },
  },
  {
    key: "c-se",
    edges: { bottom: true, right: true },
    cursor: "se-resize",
    style: {
      bottom: 0,
      right: 0,
      width: RESIZE_CORNER_SIZE,
      height: RESIZE_CORNER_SIZE,
    },
  },
  {
    key: "c-sw",
    edges: { bottom: true, left: true },
    cursor: "sw-resize",
    style: {
      bottom: 0,
      left: 0,
      width: RESIZE_CORNER_SIZE,
      height: RESIZE_CORNER_SIZE,
    },
  },
  {
    key: "c-ne",
    edges: { top: true, right: true },
    cursor: "ne-resize",
    style: {
      top: 0,
      right: 0,
      width: RESIZE_CORNER_SIZE,
      height: RESIZE_CORNER_SIZE,
    },
  },
  {
    key: "c-nw",
    edges: { top: true, left: true },
    cursor: "nw-resize",
    style: {
      top: 0,
      left: 0,
      width: RESIZE_CORNER_SIZE,
      height: RESIZE_CORNER_SIZE,
    },
  },
];

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

/**
 * Sidebar's left edge in viewport coordinates. Prefers the ref's actual rect
 * (handles `isStatic` sidebars whose width depends on the flex container) and
 * falls back to a constant reserve when the ref isn't attached yet.
 */
function getSidebarLeftEdge(
  anchorRef?: React.RefObject<HTMLDivElement | null>,
): number {
  const rect = anchorRef?.current?.getBoundingClientRect();
  if (rect && rect.width > 0) return rect.left;
  return window.innerWidth - SIDEBAR_RESERVED_WIDTH;
}

/**
 * Initial panel width — capped at `PANEL_WIDTH`, floored at `MIN_PANEL_WIDTH`,
 * and shrunk when the available area (viewport minus the actual sidebar) is
 * narrower than the default. Called from both the `panelWidth` state
 * initializer and the `position` initializer so they can't drift apart.
 */
function getInitialPanelWidth(
  anchorRef?: React.RefObject<HTMLDivElement | null>,
): number {
  const sidebarLeft = getSidebarLeftEdge(anchorRef);
  return Math.min(
    PANEL_WIDTH,
    Math.max(
      MIN_PANEL_WIDTH,
      sidebarLeft - PANEL_GAP_TO_SIDEBAR - 2 * PANEL_EDGE_MARGIN,
    ),
  );
}

function getInitialPanelHeight(): number {
  return Math.min(PANEL_HEIGHT, window.innerHeight - 2 * PANEL_EDGE_MARGIN);
}

export interface ReferencePdfFloatingViewerProps {
  title: string;
  file: Blob;
  initialPage: number;
  initialSearchQuery?: string;
  initialSearchText?: string;
  initialHighlightTerms?: readonly string[];
  requestToken: number;
  onSearch?: (query: string) => Promise<PDFSearchResult>;
  onClose: () => void;
  /**
   * Sidebar the viewer opens beside (today the IR sidebar's outer DOM node).
   * Read at mount time only, for its actual left edge, so the panel lands in
   * the non-sidebar area whether the sidebar runs in overlay (fixed 350 px) or
   * static (flex-grown) mode. Absent: the default right-hand reserve.
   */
  anchorRef?: React.RefObject<HTMLDivElement | null>;
}

/**
 * Draggable + resizable, non-blocking floating viewer for the reference PDFs
 * (AMA Guide / Rating tables). Portalled to `document.body` so it escapes any
 * transformed ancestor's stacking context — without the portal,
 * `position: fixed` would anchor to the transformed ancestor instead of the
 * viewport.
 */
export const ReferencePdfFloatingViewer: React.FC<
  ReferencePdfFloatingViewerProps
> = ({
  title,
  file,
  initialPage,
  initialSearchQuery,
  initialSearchText,
  initialHighlightTerms,
  requestToken,
  onSearch,
  onClose,
  anchorRef,
}) => {
  const [panelWidth, setPanelWidth] = useState(() =>
    getInitialPanelWidth(anchorRef),
  );
  const [panelHeight, setPanelHeight] = useState(getInitialPanelHeight);
  // Initial position: anchor the panel's right edge at the sidebar's *actual*
  // left edge (minus a small gap), so the panel sits adjacent to the sidebar
  // for side-by-side viewing — independent of viewport width or whether the
  // sidebar is in overlay (350 px) or static (flex-grown) mode. Vertically
  // centered in the viewport.
  const [position, setPosition] = useState(() => {
    const initialWidth = getInitialPanelWidth(anchorRef);
    const initialHeight = getInitialPanelHeight();
    const sidebarLeft = getSidebarLeftEdge(anchorRef);
    return {
      x: Math.max(
        PANEL_EDGE_MARGIN,
        Math.round(sidebarLeft - PANEL_GAP_TO_SIDEBAR - initialWidth),
      ),
      y: Math.max(
        PANEL_EDGE_MARGIN,
        Math.round((window.innerHeight - initialHeight) / 2),
      ),
    };
  });

  const bodyRef = useRef<HTMLDivElement | null>(null);
  const [pageWidth, setPageWidth] = useState(
    Math.max(300, PANEL_WIDTH - VIEWER_PAGE_PADDING),
  );

  // Derive `pageWidth` from both the body's width AND its height so one page
  // fits fully without internal scrolling. The height-based cap uses A4
  // portrait as the worst-case aspect ratio — landscape pages end up smaller
  // and centered, which is acceptable. Re-runs on every panel resize.
  useLayoutEffect(() => {
    const node = bodyRef.current;
    if (!node) return;
    const update = () => {
      const widthBased = node.clientWidth - VIEWER_PAGE_PADDING;
      const heightBased =
        (node.clientHeight - PDF_CHROME_HEIGHT - PAGE_VERTICAL_PADDING) /
        PAGE_ASPECT_RATIO;
      setPageWidth(
        Math.max(300, Math.floor(Math.min(widthBased, heightBased))),
      );
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const activeListenersRef = useRef<{
    move: (e: MouseEvent) => void;
    up: () => void;
  } | null>(null);

  // Applied to `document.body` during an active drag / resize. The cursor
  // override keeps the browser from snapping back to the default when the
  // pointer leaves the panel, and `pointer-events: none` stops hover-sensitive
  // elements underneath (tooltips, buttons, iframes) from intercepting
  // `mousemove` — which otherwise steals events and strands the gesture.
  const setGlobalDragStyle = (cursor: string | null): void => {
    if (cursor) {
      document.body.style.cursor = cursor;
      document.body.style.userSelect = "none";
      document.body.style.pointerEvents = "none";
    } else {
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      document.body.style.pointerEvents = "";
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-existing; deps reviewed, auto-fix risks behavior change
  useEffect(() => {
    return () => {
      if (activeListenersRef.current) {
        window.removeEventListener(
          "mousemove",
          activeListenersRef.current.move,
        );
        window.removeEventListener("mouseup", activeListenersRef.current.up);
      }
      setGlobalDragStyle(null);
    };
  }, []);

  // Close on Escape. Standard expectation for any dialog-ish floating surface,
  // non-blocking or not — user reaches for Esc to dismiss.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  const dragState = useRef<{
    startX: number;
    startY: number;
    origX: number;
    origY: number;
  } | null>(null);

  const [isDragging, setIsDragging] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-existing; deps reviewed, auto-fix risks behavior change
  const handleDragStart = useCallback(
    (e: React.MouseEvent) => {
      if ((e.target as HTMLElement).closest("button")) return;
      e.preventDefault();

      dragState.current = {
        startX: e.clientX,
        startY: e.clientY,
        origX: position.x,
        origY: position.y,
      };
      setIsDragging(true);
      setGlobalDragStyle("grabbing");

      const onMove = (ev: MouseEvent) => {
        if (!dragState.current) return;
        const dx = ev.clientX - dragState.current.startX;
        const dy = ev.clientY - dragState.current.startY;
        setPosition({
          x: clamp(
            dragState.current.origX + dx,
            0,
            Math.max(0, window.innerWidth - panelWidth),
          ),
          y: clamp(
            dragState.current.origY + dy,
            0,
            Math.max(0, window.innerHeight - DRAG_BOTTOM_OFFSET),
          ),
        });
      };

      const onUp = () => {
        dragState.current = null;
        activeListenersRef.current = null;
        setIsDragging(false);
        setGlobalDragStyle(null);
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
      };

      activeListenersRef.current = { move: onMove, up: onUp };
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
    },
    [position, panelWidth],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-existing; deps reviewed, auto-fix risks behavior change
  const handleResizeStart = useCallback(
    (edges: ResizeEdges, cursor: string) => (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      const startX = e.clientX;
      const startY = e.clientY;
      const origW = panelWidth;
      const origH = panelHeight;
      const origX = position.x;
      const origY = position.y;
      setGlobalDragStyle(cursor);

      const onMove = (ev: MouseEvent) => {
        const dx = ev.clientX - startX;
        const dy = ev.clientY - startY;
        let w = origW;
        let h = origH;
        let x = origX;
        let y = origY;

        if (edges.right) w = Math.max(MIN_PANEL_WIDTH, origW + dx);
        if (edges.left) {
          const d = Math.min(dx, origW - MIN_PANEL_WIDTH);
          w = origW - d;
          x = origX + d;
        }
        if (edges.bottom) h = Math.max(MIN_PANEL_HEIGHT, origH + dy);
        if (edges.top) {
          const d = Math.min(dy, origH - MIN_PANEL_HEIGHT);
          h = origH - d;
          y = origY + d;
        }

        setPanelWidth(w);
        setPanelHeight(h);
        setPosition({ x, y });
      };

      const onUp = () => {
        activeListenersRef.current = null;
        setGlobalDragStyle(null);
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
      };

      activeListenersRef.current = { move: onMove, up: onUp };
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
    },
    [panelWidth, panelHeight, position],
  );

  return createPortal(
    // `role="region"` (not `"dialog"`) because the panel is intentionally
    // non-blocking — sidebars and the rest of the page remain
    // interactive behind it. A dialog role implies modal/focus-trap semantics
    // that don't fit; "region" + `aria-label` communicates "supplemental
    // content area" to assistive tech.
    <div
      className="reference-pdf-floating-viewer"
      style={{
        left: position.x,
        top: position.y,
        width: panelWidth,
        height: panelHeight,
      }}
      role="region"
      aria-label={title}
    >
      <div
        className="reference-pdf-floating-viewer__header"
        onMouseDown={handleDragStart}
        style={{ cursor: isDragging ? "grabbing" : "grab" }}
      >
        <div className="reference-pdf-floating-viewer__title">{title}</div>
        <Tooltip content="Close" placement="bottom-end">
          <button
            className="reference-pdf-floating-viewer__action-btn"
            aria-label="Close PDF"
            type="button"
            onClick={onClose}
          >
            <Icon name="close" />
          </button>
        </Tooltip>
      </div>

      <div className="reference-pdf-floating-viewer__body" ref={bodyRef}>
        <Suspense
          fallback={
            <div className="flex items-center justify-center h-full">
              <LoadingPlaceholder noLogo />
            </div>
          }
        >
          <PDFViewerWithControls
            title={title}
            file={file}
            pageWidth={pageWidth}
            initialPage={initialPage}
            initialSearchQuery={initialSearchQuery}
            initialSearchText={initialSearchText}
            initialHighlightTerms={initialHighlightTerms}
            requestToken={requestToken}
            onSearch={onSearch}
          />
        </Suspense>
      </div>

      {RESIZE_HANDLES.map(({ key, edges, cursor, style }) => (
        <div
          key={key}
          onMouseDown={handleResizeStart(edges, cursor)}
          style={{ position: "absolute", cursor, ...style }}
        />
      ))}
    </div>,
    document.body,
  );
};
